import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API, api, fmtErr, fmtDate, fmtDT, fmtQty } from "../../api";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Printer, ArrowLeft, Truck } from "lucide-react";

function ShipDialog({ open, onClose, pl, onShipped }) {
  const [busy, setBusy] = useState(false);
  if (!pl) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/packing-lists/${pl.id}/ship`);
      toast.success(`${pl.pl_number} shipped — inventory deducted`);
      onShipped();
      onClose();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ship-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">Confirm Shipment</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Are you sure you want to ship this Packing List? Once shipped, the selected lots will be deducted from inventory.</p>
        <dl className="text-sm space-y-1 border border-border rounded-md p-4">
          <div className="flex justify-between"><dt className="text-slate-500">Packing List</dt><dd className="font-mono font-semibold">{pl.pl_number}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Customer</dt><dd>{pl.customer || "-"}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Invoice</dt><dd className="font-mono">{pl.invoice_number || "-"}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Total Quantity</dt><dd className="font-mono font-semibold">{fmtQty(pl.total_qty)}</dd></div>
        </dl>
        <div className="text-xs font-mono text-slate-500 max-h-28 overflow-auto">
          {pl.items && pl.items.map((i) => <div key={i.lot_id + (i.box_id || "")}>{i.lot_number}{i.box_number ? ` / ${i.box_number}` : ""} — {fmtQty(i.qty)} {i.uom}</div>)}
        </div>
        <div className="flex gap-3">
          <button data-testid="ship-cancel-button" onClick={onClose} className="flex-1 h-11 rounded-md border border-border font-heading font-bold text-sm text-slate-600">CANCEL</button>
          <button data-testid="ship-confirm-button" onClick={confirm} disabled={busy}
            className="flex-1 h-11 rounded-md font-heading font-bold text-sm text-white disabled:opacity-60" style={{ backgroundColor: "#EB1C27" }}>
            {busy ? "Shipping..." : "CONFIRM SHIP"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PackingListDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [pl, setPl] = useState(null);
  const [shipOpen, setShipOpen] = useState(false);
  const canShip = ["admin", "packing"].includes(user?.role);

  const load = () => api.get(`/packing-lists/${id}`).then((r) => setPl(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!pl) return <div className="text-slate-500" data-testid="pl-loading">Loading...</div>;

  const shipped = pl.status === "Shipped";
  const cell = "border border-slate-300 px-3 py-2 text-sm";

  return (
    <div data-testid="pl-detail-page" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 no-print">
        <Link to="/packing-list" className="h-10 px-4 rounded-md border border-border inline-flex items-center gap-2 text-sm font-semibold text-[#002B5D] bg-white">
          <ArrowLeft className="w-4 h-4" /> All Packing Lists
        </Link>
        <StatusBadge status={pl.status || "Created"} testid="pl-status" />
        {shipped && (
          <span className="text-sm text-slate-500" data-testid="pl-shipped-info">
            Shipped {fmtDT(pl.shipped_at)} by {pl.shipped_by}
          </span>
        )}
        {canShip && !shipped && (
          <button data-testid="pl-ship-button" onClick={() => setShipOpen(true)}
            className="h-10 px-5 rounded-md font-heading font-bold text-sm text-white inline-flex items-center gap-2" style={{ backgroundColor: "#002B5D" }}>
            <Truck className="w-4 h-4" /> SHIP
          </button>
        )}
        {shipped && (
          <button data-testid="pl-ship-button-disabled" disabled
            className="h-10 px-5 rounded-md font-heading font-bold text-sm bg-slate-200 text-slate-400 inline-flex items-center gap-2 cursor-not-allowed">
            <Truck className="w-4 h-4" /> SHIPPED
          </button>
        )}
        <a data-testid="pl-print-button" href={`${API}/packing-lists/${id}/pdf`} download
          className="h-10 px-5 rounded-md font-heading font-bold text-sm text-white inline-flex items-center gap-2" style={{ backgroundColor: "#EB1C27" }}>
          <Printer className="w-4 h-4" /> Download PDF
        </a>
      </div>

      <div id="print-area" className="bg-white border border-border rounded-md p-8 max-w-4xl mx-auto" data-testid="pl-document">
        <div className="flex items-center justify-between border-b-4 pb-4" style={{ borderColor: "#002B5D" }}>
          <img src="/skz-logo.png" alt="SK Zippers" className="h-16 object-contain" data-testid="pl-logo" />
          <div className="text-right">
            <div className="font-heading font-extrabold text-xl text-[#EB1C27] uppercase tracking-wide">Packing List</div>
            <div className="font-mono font-bold text-[#002B5D]" data-testid="pl-doc-number">{pl.pl_number}</div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 py-4 text-sm border-b border-slate-200">
          <div><span className="text-xs font-mono uppercase tracking-wider text-slate-400 block">Date</span><span className="font-semibold" data-testid="pl-doc-date">{fmtDate(pl.date)}</span></div>
          <div><span className="text-xs font-mono uppercase tracking-wider text-slate-400 block">Customer</span><span className="font-semibold" data-testid="pl-doc-customer">{pl.customer || "-"}</span></div>
          <div className="col-span-2"><span className="text-xs font-mono uppercase tracking-wider text-slate-400 block">Invoice Number</span><span className="font-semibold font-mono" data-testid="pl-doc-invoice">{pl.invoice_number || "-"}</span></div>
        </div>

        <table className="w-full border-collapse mt-4" data-testid="pl-doc-table">
          <thead>
            <tr className="bg-[#002B5D] text-white">
              <th className={`${cell} text-left`}>Sr.</th>
              <th className={`${cell} text-left`}>Lot Number</th>
              <th className={`${cell} text-left`}>Product</th>
              <th className={`${cell} text-right`}>Quantity</th>
              <th className={`${cell} text-right`}>Joints</th>
              <th className={`${cell} text-right`}>Net Weight</th>
              <th className={`${cell} text-right`}>Gross Weight</th>
            </tr>
          </thead>
          <tbody>
            {(pl.items || []).map((i, idx) => (
              <tr key={i.lot_id + (i.box_id || "")} className={idx % 2 ? "bg-slate-50" : ""} data-testid={`pl-doc-row-${i.lot_number}${i.box_number ? `-${i.box_number}` : ""}`}>
                <td className={cell}>{idx + 1}</td>
                <td className={`${cell} font-mono font-semibold`}>{i.lot_number}</td>
                <td className={cell}>{i.product_name} <span className="text-slate-400 text-xs font-mono">{i.product_code}</span></td>
                <td className={`${cell} text-right font-mono`}>{fmtQty(i.qty)} {i.uom}</td>
                <td className={`${cell} text-right font-mono`}>{i.joints ?? "-"}</td>
                <td className={`${cell} text-right font-mono`}>{i.net_weight ?? "-"}</td>
                <td className={`${cell} text-right font-mono`}>{i.gross_weight ?? "-"}</td>
              </tr>
            ))}
            <tr className="bg-slate-100 font-bold">
              <td className={cell} colSpan={3}>TOTALS</td>
              <td className={`${cell} text-right font-mono`} data-testid="pl-doc-total-qty">{fmtQty(pl.total_qty)}</td>
              <td className={cell}></td>
              <td className={`${cell} text-right font-mono`} data-testid="pl-doc-total-net">{fmtQty(pl.total_net ?? pl.items.reduce((a, i) => a + (parseFloat(i.net_weight) || 0), 0))} KG</td>
              <td className={`${cell} text-right font-mono`} data-testid="pl-doc-total-gross">{fmtQty(pl.total_gross ?? pl.items.reduce((a, i) => a + (parseFloat(i.gross_weight) || 0), 0))} KG</td>
            </tr>
          </tbody>
        </table>

        {pl.notes && <p className="text-sm text-slate-600 mt-4">Notes: {pl.notes}</p>}

        <div className="flex justify-between mt-16 text-sm" data-testid="pl-signatures">
          <div>
            <div className="font-semibold">POA (Person of Authority)</div>
            <div className="mt-8">Signature: __________________</div>
          </div>
          <div>
            <div className="font-semibold">Receiver's Signature</div>
            <div className="mt-8">Signature: __________________</div>
          </div>
        </div>
      </div>

      <ShipDialog open={shipOpen} onClose={() => setShipOpen(false)} pl={pl} onShipped={load} />
    </div>
  );
}
